import React, { useEffect, useState } from 'react';
import projectAPI from '../api/projectAPI'; 
import workerAPI from '../api/workerAPI';
import { FaEdit, FaTrash } from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import UserDashboard from './UserDashboard';

const TeamProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: [],
    startDate: '',
    endDate: '',
    status: '',
  });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const loggedWorker = JSON.parse(localStorage.getItem("worker"));

  useEffect(() => {
    fetchWorkers();
    fetchProjects();
  }, []);

  const fetchWorkers = async () => {
    try {
      const res = await workerAPI.get('/');
      let filteredWorkers = res.data;

      if (loggedWorker?.role === "TL") {
        filteredWorkers = filteredWorkers.filter(
          (w) => w.role === "Developer" && w.team === loggedWorker.team
        );
      } else if (loggedWorker?.role === "Developer") {
        filteredWorkers = filteredWorkers.filter(
          (w) => w._id === loggedWorker._id
        );
      }
      // Admin/HR → sees all

      setWorkers(filteredWorkers);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await projectAPI.get('/');
      let allProjects = res.data;

      if (loggedWorker?.role === 'TL') {
        allProjects = allProjects.filter(project =>
          project.assignedTo?.some(user =>
            user.role === 'Developer' && user.team === loggedWorker.team
          )
        );
      } else if (loggedWorker?.role === 'Developer') {
        allProjects = allProjects.filter(project =>
          project.assignedTo?.some(user => user._id === loggedWorker._id)
        );
      }

      setProjects(allProjects);
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (workerId) => {
    const updated = formData.assignedTo.includes(workerId)
      ? formData.assignedTo.filter((id) => id !== workerId)
      : [...formData.assignedTo, workerId];
    setFormData({ ...formData, assignedTo: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProjectId) {
        await projectAPI.put(`/${editingProjectId}`, formData);
        alert('Project updated successfully');
        setEditingProjectId(null);
      } else {
        await projectAPI.post('/', formData);
      }
      setFormData({
        title: '',
        description: '',
        assignedTo: [],
        startDate: '',
        endDate: '',
        status: ''
      });
      fetchProjects();
      setDropdownOpen(false);
    } catch (err) {
      console.error('Error saving project:', err);
    }
  };

  const handleEdit = (project) => {
    setFormData({
      title: project.title,
      description: project.description,
      assignedTo: project.assignedTo.map((w) => typeof w === "object" ? w._id : w),
      startDate: project.startDate,
      endDate: project.endDate,
      status: project.status,
    });
    setEditingProjectId(project._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await projectAPI.delete(`/${id}`);
        fetchProjects();
      } catch (err) {
        console.error('Error deleting project:', err);
      }
    }
  };

  const chartData = [
    { status: 'Ongoing', count: projects.filter(p => p.status === 'Ongoing').length },
    { status: 'Completed', count: projects.filter(p => p.status === 'Completed').length },
    { status: 'On Hold', count: projects.filter(p => p.status === 'On Hold').length },
  ];

  return (
    <div className="min-h-screen bg-purple-100 p-6 pt-20">
      <UserDashboard />

      <div className="max-w-7xl mx-auto flex justify-between items-center mb-5 mt-4">
        <h1 className="text-3xl font-bold font-mono text-purple-700">Project Tracking</h1>
        <button
          onClick={() => setShowChart(!showChart)}
          className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600"
        >
          {showChart ? 'Back to Projects' : 'View Chart'}
        </button>
      </div>

      {showChart ? (
        <div className="bg-white p-6 rounded-xl shadow max-w-4xl mx-auto mt-14">
          <h2 className="text-xl font-semibold text-purple-600 mb-4">Project Status Chart</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <XAxis dataKey="status" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#9F7AEA" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Form Panel */}
          <div className="bg-white p-4 rounded-xl shadow">
            <h2 className="text-xl font-semibold mb-4 text-purple-600">Add New Project</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
  {/* Project Title */}
  <div>
    <label htmlFor="title" className="block text-sm font-medium text-gray-700 ">
      Project Title
    </label>
    <input
      id="title"
      type="text"
      name="title"
      value={formData.title}
      onChange={handleInputChange}
      className="border border-gray-300 px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
      required
    />
  </div>

  {/* Description */}
  <div>
    <label htmlFor="description" className="block text-sm font-medium text-gray-700 ">
      Project Description
    </label>
    <textarea
      id="description"
      name="description"
      value={formData.description}
      onChange={handleInputChange}
      rows={1}
      className="border border-gray-300 px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
      required
    />
  </div>

  {/* Assigned Workers */}
  <div>
    <label className="block text-sm font-medium text-gray-700 ">Assign To</label>
    <button
      type="button"
      onClick={() => setDropdownOpen(!dropdownOpen)}
      className="border px-4 py-2 rounded-lg w-full text-left bg-white"
    >
      {formData.assignedTo.length === 0
        ? 'Select workers'
        : `${formData.assignedTo.length} worker(s) selected`}
    </button>
    {dropdownOpen && (
  <div className="mt-1 border rounded-lg bg-white shadow z-20 absolute max-h-28 overflow-y-auto">
        {workers.map((worker) => (
          <label key={worker._id} className="block px-4 py-2 hover:bg-gray-100 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.assignedTo.includes(worker._id)}
              onChange={() => handleCheckboxChange(worker._id)}
              className="mr-2"
            />
            {worker.username} ({worker.role})
          </label>
        ))}
      </div>
    )}
  </div>

  {/* Start Date & End Date */}
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 ">
        Start Date
      </label>
      <input
        id="startDate"
        type="date"
        name="startDate"
        value={formData.startDate}
        onChange={handleInputChange}
        className="border border-gray-300 px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </div>

    <div>
      <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 ">
        End Date
      </label>
      <input
        id="endDate"
        type="date"
        name="endDate"
        value={formData.endDate}
        onChange={handleInputChange}
        className="border border-gray-300 px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </div>
  </div>

  {/* Status */}
  <div>
    <label htmlFor="status" className="block text-sm font-medium text-gray-700 ">
      Status
    </label>
    <select
      id="status"
      name="status"
      value={formData.status}
      onChange={handleInputChange}
      className="border border-gray-300 px-4 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
      required
    >
      <option className='text-sm' value="" disabled hidden>Select Status</option>
      <option className='text-sm' value="Ongoing">Ongoing</option>
      <option className='text-sm' value="Completed">Completed</option>
      <option className='text-sm' value="On Hold">On Hold</option>
    </select>
  </div>

  {/* Submit Button */}
  <button
    type="submit"
    className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 w-full"
  >
    {editingProjectId ? 'Update Project' : 'Save Project'}
  </button>
</form>


          </div>

          {/* Table Panel */}
          <div className="bg-white p-4 rounded-xl shadow overflow-x-auto">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
              <h3 className="text-xl font-semibold text-purple-600">Assigned Projects</h3>
              <input
                type="text"
                placeholder="Search by Project and Assignee Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border px-4 py-2 rounded-lg w-full md:w-80"
              />
            </div>

            <div className="overflow-y-auto max-h-[400px]">
              <table className="min-w-full border text-left text-sm">
                <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
                  <tr>
                    <th className="p-2">Title</th>
                    <th className="p-2">Assigned</th>
                    <th className="p-2">Start</th>
                    <th className="p-2">End</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects
                    .filter((project) => {
                      const titleMatch = project.title?.toLowerCase().includes(searchTerm.toLowerCase());
                      const assigneeMatch = project.assignedTo?.some((user) =>
                        typeof user === 'object' &&
                        user.username?.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      return titleMatch || assigneeMatch;
                    })
                    .map((project) => (
                      <tr key={project._id} className="border-t">
                        <td className="p-2">{project.title}</td>
                        <td className="p-2">
                          {project.assignedTo?.length > 0
                            ? project.assignedTo
                                .map((user) =>
                                  typeof user === 'object' ? user.username : 'Unknown'
                                )
                                .join(', ')
                            : 'Unknown'}
                        </td>
                        <td className="p-2">{project.startDate?.slice(0, 10) || 'Unknown'}</td>
                        <td className="p-2">{project.endDate?.slice(0, 10) || 'Unknown'}</td>
                        <td
                          className={`py-2 px-4 font-semibold ${
                            project.status === 'Completed'
                              ? 'text-green-600'
                              : project.status === 'On Hold'
                              ? 'text-blue-600'
                              : 'text-yellow-600'
                          }`}
                        >
                          {project.status}
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => handleEdit(project)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(project._id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamProjectManager;
